/**
 * Node.JS/NPM Modules
 */
const uuid = require('node-uuid');
const eventEmitter = require('events')
  .EventEmitter;

/**
 * Loacl Modules
 */
const signaling_module = require('./signaling_module');
const events = require('./events');
const peer_connection = require('./peer_connection');
const media_manager = require('./media_manager');
const logger = require('./logger');
const browserDetector = require('./browser_detector');
/**
 * Module Variables
 */
const TAG = '[switchrtc_core]';
const MAX_BANDW = 4000;
const MIN_BANDW = 128;
let event_manager;
let inCall;
let isConnected;
let isConnectedToRoom;
let peerId = uuid.v4();
let userId;
let nickname;
let config;
let lastStats;
let activePermissions;
let isRecording;
let recordingStatus;
let screenShares;
let remoteStreams = [];
let queryParams = {};
/**
 * Module Functions
 */

/**
 * To be able to use the SwitchRTC one must initialize it first.
 * this function will make sure that the peer is connected to the signaling server via WS.
 * > Note: You must call this function before calling any other function of this SDK
 * @param  {String} signalingWS signaling server WebSocketURL
 * @throws {Error} Invalid URL param
 * @event RECONNECTED, CONNECTED
 * @example ```
 * try{
 *      switchrtc.init('wss://www.example_signaling_url.com:9898');
 * }catch(function(err){
 *  // Handle Error
 * });
 * ```
 */
let init = (signalingWS, _queryParams) => {
  if (!signalingWS) {
    throw new Error('Invalid signaling server URL.');
  }
  // if (!browserDetector.isChrome) {
  //     throw new Error('Unsupported browser, please use Google Chrome or Chromium');
  // }
  event_manager = new eventEmitter();
  inCall = false;
  isConnected = false;
  isConnectedToRoom = false;

  nickname = 'peer-' + peerId.substring(0, 5);
  config = {};
  lastStats = null;
  activePermissions = {
    isHost: false,
    recvOnly: false,
    audioOnly: false,
    screenShare: true,
    recordSession: false
  };
  isRecording = false;
  recordingStatus = null;
  screenShares = [];
  remoteStreams = [];
  if(_queryParams) queryParams = _queryParams;
  signaling_module.init(event_manager, _onConnectCall, _onDisconnectCall, _onIceConfig, _onPermissionsUpdate, _onAudioMute);
  media_manager.init(event_manager, _gotLocalStream);
  signaling_module.connect({
    signalingWS: signalingWS
  }, false,queryParams,() => {
    isConnected = true;
    event_manager.emit(events.CONNECTED);
  });
  event_manager.on(events.GOT_SCREEN_SHARE_STREAM, _gotLocalScreenStream);
  event_manager.on(events.SCREEN_SHARE_STOPPED, _stoppedScreenShare);
  event_manager.on(events.SESSION_RECORDING_ON, _sessionRecordOn);
  event_manager.on(events.SESSION_RECORDING_OFF, _sessionRecordOff);
  event_manager.on(events.SESSION_RECORDING_PAUSED, _sessionRecordPaused);
  event_manager.on(events.SESSION_RECORDING_RESUMED, _sessionRecordResumed);
  event_manager.on(events.SESSION_INFO_UPDATE, _sessionInfoUpdate);
};

/**
 * This function will connect you to a session of your choosing.
 * Will get your local media (if wanted) and adding it to the session.
 * @throws {Error} Invalid maxBw parameter. MaxBw mut be: 128 <= MaxBw <=2048. OR Must Init First
 * @param  {String} _roomId   room id. (Room name)
 * @param  {Number} _maxBw    in kb. defaults to 512
 * @param  {Boolean} _recvOnly true if you want to receive only (without sending), false for 2 way media share.
 * @param  {String} _token    access token
 */
let connectToRoom = (connectParams) => {
  if (!isConnected) throw new Error(TAG + ' Must Init First!');
  if (isConnectedToRoom) throw new Error(TAG + ' ERROR: Called connectToRoom() more than once. call dispose() before connecting to a session again');
  // allow overriding with custom peerId
  peerId = connectParams.peerId !== undefined ? connectParams.peerId : peerId;
  userId = connectParams.userId !== undefined ? connectParams.userId : userId;
  nickname = userId.substring(0, 8);
  logger.debug(TAG, 'connectToRoom params:', JSON.stringify(connectParams));
  config.roomId = connectParams.roomId || 'demo';
  config.maxBw = connectParams.maxBw || 1000;
  config.token = connectParams.token;
  config.recvOnly = connectParams.recvOnly || false;
  config.sendOnly = connectParams.sendOnly || false;
  config.sendAudio = connectParams.sendAudio !== undefined ? connectParams.sendAudio : true;
  config.audioSource = connectParams.audioSource || null;
  config.sendVideo = connectParams.sendVideo !== undefined ? connectParams.sendVideo : true;
  config.videoSource = connectParams.videoSource || null;
  config.hdVideo = connectParams.hdVideo !== undefined ? connectParams.hdVideo : true;
  config.skipjoin = connectParams.skipjoin || null;
  config.isRecorder = connectParams.isRecorder;
  //Optional - custom mediaConstraints. will be passed to getUserMedia
  config.mediaConstraints = connectParams.mediaConstraints;
  //Optional - peerConnectionConfig
  config.peerConnectionConfig = connectParams.peerConnectionConfig;
  config.maxBundle = connectParams.maxBundle !== undefined ? connectParams.maxBundle : false;

  if (config.maxBw < MIN_BANDW || config.maxBw > MAX_BANDW)
    throw new Error(TAG + ' Invalid maxBw parameter:' + config.maxBw);
  logger.debug(TAG, 'connectToRoom, peerId <', peerId, '> userId <', userId, '> resulting config:', JSON.stringify(config));
  _start();
};
/**
 * Disconnect from session, deletes streams, disconnects from signaling server.
 * @event DISCONNECTED
 */
let dispose = () => {
  return new Promise((resolve, reject) => {
    signaling_module.closeConnection();
    isConnected = false;
    isConnectedToRoom = false;
    media_manager.clear();
    peer_connection.closePeerConnection();
    let evnts = Object.values(events);
    evnts.forEach((e) => {
      event_manager.removeAllListeners(e);
    });
    remoteStreams = [];
    logger.log(TAG, 'SwitchRTC Client Disposed...');
    peerId = uuid.v4();
    resolve();
  });
};

/**
 * This function will try to start local media.
 * @event GOT_LOCAL_STREAM
 */
let startLocalMedia = (sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints) => {
  logger.debug(TAG, 'startLocalMedia');
  config.recvOnly = false;
  peer_connection.setRecvOnly(config.recvOnly);
  media_manager.startLocalMedia(sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints, _gotLocalStream);
};

/**
 * This function will stop all local media
 * @event LOCAL_STREAM_STOPPED
 */
let stopLocalMedia = () => {
  logger.debug(TAG, 'stopLocalMedia');
  config.recvOnly = true;
  peer_connection.setRecvOnly(config.recvOnly);
  media_manager.stopLocalMedia();
};

let stopStream = (id) => {
  logger.debug(TAG, 'stopStream(', id, ')');
  media_manager.stopLocalStreamById(id);
  signaling_module.send(JSON.stringify({
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    stream: {
      added: false,
      id: id
    }
  }));
  if (media_manager.getLocalStreams.length === 0) {
    config.recvOnly = true;
    peer_connection.setRecvOnly(config.recvOnly);
  }
};

/**
 * Use this function to change you input devices. (such as cameras).
 * you must provide valid sources ids. (you can get them from the function enumerateDevices())
 * @param  {String} audioSource audio device (input) id
 * @param  {String} videoSource video device (input) id
 */
let changeDevice = (streamId, sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints) => {
  let streamConfig = {};
  streamConfig.sendAudio = sendAudio;
  streamConfig.audioSource = audioSource;
  streamConfig.sendVideo = sendVideo;
  streamConfig.videoSource = videoSource;
  streamConfig.hdVideo = hdVideo;
  media_manager.changeDevice(streamId, streamConfig.sendAudio, streamConfig.audioSource, streamConfig.sendVideo, streamConfig.videoSource, streamConfig.hdVideo, mediaConstraints, _gotLocalStream);
  signaling_module.send(JSON.stringify({
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    stream: {
      added: false,
      id: streamId
    }
  }));
};

/**
 * This function will try to start screen share.
 * @event SCREEN_SHARE_EXTENSION_ERROR, SCREEN_SHARE_STOPPED, GOT_SCREEN_SHARE_STREAM, SCREEN_SHARE_DIALOG_CANCELED
 */
let startScreenShare = () => {
  logger.debug(TAG, 'In startScreenShare');
  if (media_manager.isScreenShared() || !activePermissions.screenShare) return;
  media_manager.toggleScreenShare((success) => {
    if (!success) event_manager.emit(events.SCREEN_SHARE_EXTENSION_ERROR);

  });
};

/**
 * This function will stop screen share
 */
let stopScreenShare = () => {
  logger.debug(TAG, 'In stopScreenShare');
  if (!media_manager.isScreenShared()) return;
  media_manager.toggleScreenShare();
};



/**
 * This function will start recording
 */
let startRecording = (dynamicParam) => {
  logger.debug(TAG, 'In startRecording');
  if (!activePermissions.recordSession) {
    logger.debug(TAG, 'User has no Recording permissions');
    return;
  }
  if (isRecording || recordingStatus === 'pending')
    return;
  let signal = {
    action: 'start_record_session',
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    dynamicParam: dynamicParam || null
  };
  // Signal to server to start recording
  signaling_module.send(JSON.stringify(signal));
  recordingStatus = 'pending';
  // Get Ack from server - set recording to true - Via event_manager
};

/**
 * This function will stop recording
 */
let stopRecording = () => {
  if (!isRecording || recordingStatus === 'pending' || !activePermissions.recordSession) return;
  logger.debug(TAG, 'In stopRecording');
  let signal = {
    action: 'stop_record_session',
    roomId: config.roomId,
    peerId: peerId
  };

  // Signal to server to stop recording
  signaling_module.send(JSON.stringify(signal));
  recordingStatus = 'pending';
  // Get Ack from server - set recording to false - Via event_manager
};

let pauseRecording = () => {
  if (!isRecording || recordingStatus === 'paused' || recordingStatus === 'pending' || !activePermissions.recordSession) return;
  logger.debug(TAG, 'in pauseRecording');
  let signal = {
    action: 'pause_record_session',
    roomId: config.roomId,
    peerId: peerId
  };

  // Signal server to pause recording
  signaling_module.send(JSON.stringify(signal));
  //
};

let resumeRecording = () => {
  if (!isRecording || recordingStatus !== 'paused' || !activePermissions.recordSession) return;
  logger.debug(TAG, 'in resumeRecording');
  let signal = {
    action: 'resume_record_session',
    roomId: config.roomId,
    peerId: peerId
  };

  // Signal server to pause recording
  signaling_module.send(JSON.stringify(signal));
  //
};

/**
 * this will allow users with recording and host permissions to send custom commands to the recorder end point.
 * a command contains a 'name', 'peerId' and an optional 'data'.
 * @param  {string} commandName the name of the command
 * @param  {any} data        any data you want to pass to the recorder.
 * @return null
 */
let sendRecordingCustomCommand = (name, data) => {
  if (!isRecording || !activePermissions.recordSession || !activePermissions.isHost) return;
  let signal = {
    action: 'recorder_custom_command',
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    command: {
      name: name,
      peerId: peerId
    }
  };
  if(data) signal.command.data = data;
  signaling_module.send(JSON.stringify(signal));
};

/**
 * Returns a promise with audio/video devices.
 * @return {Promise} with the devicelist
 * @example ```
 *
 * switchrtc.enumerateDevices()
     .then(function(devices){
     // Do Stuff
        })
     .catch(function(err){
     //Handle Error
    });
 * ```
 */
let enumerateDevices = () => {
  return navigator.mediaDevices.enumerateDevices();
};

let toggleVideoMute = (streamId, isOn) => {
  return media_manager.toggleVideoMute(streamId, isOn);
};

/**
 * returns a Promise with the streams stats.
 * @return {Promise} will return a detailed stats object.
 * looks like this:
 * {
 *  connectionType: <string>,
 *  localStream: [track1, track2 ...],
 *  remoteStreams: [track1, track2 ...],
 *  timestamp: <timestamp>
 * }
 */
let getStats = () => {
  return _getStats();
};

let getStats2 = () =>{
  return _getStats2();
}

/**
 * Returns the signaling id
 * @return {String} signaling id
 */
let getSignalingId = () => {
  return signaling_module.getSignalingId();
};

/**
 * Returns the signaling version
 * @return {String} signaling version
 */
let getSignalingVersion = () => {
  return signaling_module.getSignalingVersion();
};

/**
 * Returns the media server id
 * @return {String} media server id
 */
let getMediaServerId = () => {
  return signaling_module.getMediaServerId();
};

/**
 * Returns the media server version
 * @return {String} media server version
 */
let getMediaServerVersion = () => {
  return signaling_module.getMediaServerVersion();
};

let getMaxBw = () => {
  return signaling_module.getMaxBw() || config.maxBw;
};

let setPeerNickname = (_nickname) => {
  nickname = _nickname;
  let signal = {
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    peerNickname: _nickname
  };
  logger.debug(TAG, 'Setting nickname. signal:\n', JSON.stringify(signal, null, 2));
  signaling_module.send(JSON.stringify(signal));
};

let getPeerNickname = () => nickname;

let sendChatMessage = (message) => {
  let signal = {
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    chat: {
      message: message
    }
  };
  logger.debug(TAG, 'Sending chat message. signal:\n', JSON.stringify(signal, null, 2));
  signaling_module.send(JSON.stringify(signal));
};

let getDevicePreview = (sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints) => {
  return media_manager.getLocalDevicePreview(sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints);
};

let removePreview = () => {
  media_manager.stopLocalDevicePreview();
};

let addPreviewToSession = () => {
  media_manager.addLocalDevicePreviewToSession();
};

let subscribeToPeer = (subscribePeerId) => {
  logger.debug(TAG, 'Subscribing to peer:', subscribePeerId);
  const signal = {
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
  };
  if(Array.isArray(subscribePeerId)) signal.subscribePeerIds = subscribePeerId;
  else signal.subscribePeerId = subscribePeerId;
  signaling_module.send(JSON.stringify(signal));
};

let unsubscribeFromPeer = (unsubscribePeerId) => {
  logger.debug(TAG, 'Unsubscribing from peer:', unsubscribePeerId);
  const signal = {
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
  };
  if(Array.isArray(unsubscribePeerId)) signal.unsubscribePeerIds = unsubscribePeerId;
  else signal.unsubscribePeerId = unsubscribePeerId;
  signaling_module.send(JSON.stringify(signal));
};

let enterStage = () => {
  logger.debug(TAG, 'Entering stage');
  const signal = {
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    onStage: true
  };
  signaling_module.send(JSON.stringify(signal));
}

let exitStage = () => {
  logger.debug(TAG, 'Exiting stage');
  const signal = {
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    onStage: false
  };
  signaling_module.send(JSON.stringify(signal));
}

/**
 * Private Functions
 *
 */

let _getStats = () => {
  return new Promise((resolve, reject) => {
    //logger.debug(TAG, 'Getting Stats from PeerConnection');
    peer_connection.getStats()
      .then((e) => {
        //logger.debug(TAG, 'Got Stats:', e);
        if (!browserDetector.isChrome) reject('Stats Not Supported for this Browser');
        if (e) {
          var selectedCandPair;
          var stats = {
            timestamp: (new Date())
              .getTime(),
            localStreams: [],
            remoteStreams: [],
            connectionType: ''
          };
          //console.log("===BEGIN===");
          e.result()
            .forEach(function(report) {
              //report.names().forEach(function(name) {
              //    console.log(report.id + " / " + report.type + " / " + name + " / " + report.stat(name));
              //});
              //console.log("====");
              let stat = {};
              let isLocal = false;
              stat.trackId = report.stat("googTrackId");
              // logger.debug(report);

              if (report.type == "googComponent" && report.stat("selectedCandidatePairId")) {
                selectedCandPair = report.stat("selectedCandidatePairId");
              } else if (report.type == "ssrc") {
                if (report.stat("mediaType") == "audio") {
                  if (report.stat("packetsSent")) isLocal = true;
                  stat.type = 'audio';
                  stat.audioLevel = report.stat("audioOutputLevel");
                  stat.codec = report.stat('googCodecName');
                  if (report.stat("googResidualEchoLikelihoodRecentMax")) {
                    stat.echo = report.stat("googResidualEchoLikelihoodRecentMax");
                  }

                } else if (report.stat("mediaType") == "video") {
                  stat.packetsTotal = 0;
                  stat.bytesTotal = 0;
                  stat.resolution = "";
                  stat.display = false;
                  stat.type = 'video';

                  if (report.stat("packetsSent")) isLocal = true;
                  stat.codec = report.stat('googCodecName');
                  stat.packetsLost = Number(report.stat("packetsLost"));
                  if (isLocal) {
                    stat.packetsTotal = stat.packetsLost + Number(report.stat("packetsSent"));
                    stat.bytesTotal = Number(report.stat("bytesSent"));
                    stat.frameRate = report.stat("googFrameRateSent");
                    stat.resolution = report.stat("googFrameWidthSent") + "x" + report.stat("googFrameHeightSent");
                    stat.display = (report.stat("googFrameWidthSent") > 0);
                  } else {
                    stat.packetsTotal = stat.packetsLost + Number(report.stat("packetsReceived"));
                    stat.bytesTotal = Number(report.stat("bytesReceived"));
                    stat.frameRate = report.stat("googFrameRateReceived");
                    stat.resolution = report.stat("googFrameWidthReceived") + "x" + report.stat("googFrameHeightReceived");
                    stat.display = (report.stat("googFrameWidthReceived") > 0);
                  }
                }

                stat.streamId = peer_connection.getStreamIdFromTrackId(stat.trackId, stat.type === 'video');
                if (lastStats) { //bitrate and stuff
                  var millisecFromLastCheck = stats.timestamp - lastStats.timestamp;
                  var lastStat = isLocal ?
                    lastStats.localStreams.filter((s) => s.trackId == stat.trackId)[0] :
                    lastStats.remoteStreams.filter((s) => s.trackId == stat.trackId)[0];

                  if (lastStat) {
                    stat.lastTotalPackets = stat.packetsTotal - lastStat.packetsTotal;
                    stat.lastLostPackets = stat.packetsLost - lastStat.packetsLost;
                    stat.lastTotalBytes = stat.bytesTotal - lastStat.bytesTotal;
                    stat.packetLossPct = 0;
                    if (stat.lastLostPackets > 0 && stat.lastTotalPackets > 0) {
                      stat.packetLossPct = Math.round(100.0 * stat.lastLostPackets / (stat.lastTotalPackets));
                      if (stat.packetLossPct > 100) stat.packetLossPct = 100;
                    }
                    stat.millisecFromLastCheck = millisecFromLastCheck;
                    stat.bitRate = Math.round(stat.lastTotalBytes * 8.0 / millisecFromLastCheck); // bits per milliseconds equals to kbits per second
                  }
                }

                if (stat.type && stat.streamId) {
                  stat.bitRate = stat.bitRate || "<N/A>";
                  if (isLocal) stats.localStreams.push(stat);
                  else stats.remoteStreams.push(stat);
                }
              }
            });
          //console.log("====END====");
          var report = e[selectedCandPair];
          if (report) {
            stats.connectionType = report.stat("googLocalCandidateType");
          } else {
            stats.connectionType = "N/A";
          }

          lastStats = stats;
          resolve(stats);
        } else {
          reject('[SwitchRTC]: No Stats Yet');
        }
      })
      .catch(e=>reject(e));;


  });
};

let _getStats2 = () => {
  return new Promise((resolve, reject) => {
    peer_connection.getStats2()
    .then((stats) => {
      stats.forEach(s=>s.forEach(r=>{if(r.type=="track")console.log(r);}));
    })
    .catch(e=>reject(e));
  });
};

let _start = () => {

  // for testing: change skipjoin to true to force first message with sdp instead of join request
  // note: this will disable recvonly mode and doesn't use ice servers provided by the server
  // need also to disable sending iceServers in media_servers_manager.js, otherwise two peer connections will be created
  if (config.skipjoin) {
    // explicitly simulate the events that happen when response to join is received
    _onIceConfig(null);
    _onConnectCall();
    return;
  }

  if (config.isRecorder) _initRecorder();

  signaling_module.send(JSON.stringify({
    'join': true,
    'roomId': config.roomId,
    'userId': userId,
    'peerId': peerId,
    'recvOnly': config.recvOnly,
    'sendOnly': config.sendOnly,
    'token': config.token || "",
    'maxBw': config.maxBw,
    'isRecorder': config.isRecorder,
    'sdpSemantics': config.peerConnectionConfig ? config.peerConnectionConfig.sdpSemantics : undefined
  }));
};

let _initRecorder = () => {
  // Init the recorder session
  // Create a file in Downloads folder "running.txt"
  var blob = new Blob([], {
    'type': 'text/txt'
  });
  var blobUrl = window.URL.createObjectURL(blob);
  _downloadURI(blobUrl, "running.txt");
};

let _downloadURI = (uri, name) => {
  var link = document.createElement("a");
  link.download = name;
  link.href = uri;
  link.click();
};

let _onPermissionsUpdate = (permissions) => {
  activePermissions = permissions || activePermissions;

  // apply new permissions
  if (activePermissions.recvOnly)
    stopLocalMedia();

  if (!activePermissions.screenShare)
    stopScreenShare();


  logger.debug(TAG, 'active Permissions: ', activePermissions);
  event_manager.emit(events.SESSION_PERMISSIONS_UPDATE, activePermissions);
};

let _onIceConfig = (iceServers) => {
  if (peer_connection.getPeerConnection()) {
    return;
  }

  // got ice config, can create peer_connection
  let pcOptions = {
    gotIceCandidate: _gotIceCandidate,
    gotRemoteStream: _gotRemoteStream,
    lostRemoteStream: _lostRemoteStream,
    roomId: config.roomId,
    maxBw: config.maxBw,
    recvOnly: config.recvOnly,
    userId: userId,
    peerId: peerId,
    iceServers: iceServers,
    peerConnectionConfig: config.peerConnectionConfig,
    notifyICEReset: _notifyICEReset,
    notifyConnected: _notifyConnected,
    maxBundle: config.maxBundle
  };
  peer_connection.createPeerConnection(pcOptions);
};

let _onAudioMute = (isMuted) => {
  logger.debug(TAG, '_onAudioMute isMuted=', isMuted);
  media_manager.toggleAudioMute(null, isMuted).then(() => {
    event_manager.emit(events.AUDIO_MUTED, isMuted);
  }).catch((err) => {
    logger.error(TAG, err);
  });
};

let _onConnectCall = () => {
  if (!config.recvOnly && !activePermissions.recvOnly) {
    logger.debug(TAG, 'Connecting as sendrecv');
    startLocalMedia(config.sendAudio, config.audioSource, config.sendVideo, config.videoSource, config.hdVideo, config.mediaConstraints);
  } else {
    logger.debug(TAG, 'Connecting as recvonly');
  }
};

let _onDisconnectCall = (reason) => {
  media_manager.clear();
  event_manager.emit(events.CALL_ENDED, reason);
};

let _gotLocalStream = (stream, err, isPreview) => {
  logger.debug(TAG, 'Got Local stream, ', stream);
  if (err) {
    logger.debug(TAG, 'error in getting local stream', err);
    event_manager.emit(events.LOCAL_STREAM_ERROR, err);
  } else if (peer_connection.getPeerConnection() && stream) {
    logger.debug(TAG, 'Adding localstream to peerConnection');
    peer_connection.getPeerConnection().addStream(stream);
    // stream.getTracks().forEach(track => peer_connection.getPeerConnection().addTrack(track, stream));
    if (!isPreview) signaling_module.send(JSON.stringify({
      roomId: config.roomId,
      userId: userId,
      peerId: peerId,
      stream: {
        added: true,
        id: stream.id
      }
    }));
    event_manager.emit(events.GOT_LOCAL_STREAM, stream);
  }
};

let _gotLocalScreenStream = (stream) => {
  if (peer_connection.getPeerConnection() && stream) {
    let pc = peer_connection.getPeerConnection();
    // stream.getTracks().forEach(track => pc.addTrack(track, stream));
    pc.addStream(stream);
    // Signal the signaling server that this stream is a screen share.
    signaling_module.send(JSON.stringify({
      roomId: config.roomId,
      userId: userId,
      peerId: peerId,
      screenShare: {
        add: true,
        stream: {
          id: stream.id
        }
      }
    }));
  }
};

let _stoppedScreenShare = (streamId) => {
  // Signal the signaling server that this screen share is done.
  logger.debug(TAG, 'Sending stoppedScreenShare');
  signaling_module.send(JSON.stringify({
    roomId: config.roomId,
    userId: userId,
    peerId: peerId,
    screenShare: {
      add: false,
      stream: {
        id: streamId
      }
    }
  }));
};

let _gotIceCandidate = (ice) => {
  if (ice.candidate) {
    signaling_module.send(JSON.stringify({
      'ice': ice.candidate,
      'roomId': config.roomId,
      'userId': userId,
      'peerId': peerId
    }));
  }
};

let _gotRemoteStream = (event) => {
  var stream = event.streams[0];
  logger.debug(TAG, 'got remote stream, ID:', stream.id);
  if (remoteStreams.indexOf(stream.id) < 0) {
    remoteStreams.push(stream.id);
    logger.debug(TAG, 'emitting', events.GOT_REMOTE_STREAM, 'for', stream.id);
    event_manager.emit(events.GOT_REMOTE_STREAM, stream);
  }
};

let _lostRemoteStream = (event) => {
  logger.debug(TAG, 'Remote stream Lost, ID:', event.stream.id);
  var index = remoteStreams.indexOf(event.stream.id);
  if (index >= 0) {
    delete remoteStreams[index];
    logger.debug(TAG, 'emitting', events.REMOTE_STREAM_LOST, 'for', event.stream.id);
    event_manager.emit(events.REMOTE_STREAM_LOST, event.stream.id);
  }
};

let _sessionRecordOn = (event) => {
  isRecording = event.action_response !== 'FAIL';
  recordingStatus = event.action_response === 'FAIL' ? null : 'recording';
};

let _sessionRecordOff = (event) => {
  isRecording = false;
  recordingStatus = null;
};

let _sessionRecordPaused = (event) => {
  recordingStatus = 'paused';
};

let _sessionRecordResumed = (event) => {
  recordingStatus = 'recording';
};

let _sessionInfoUpdate = (event) => {
  if (event.recorder) { // Recorder Updates...
    isRecording = true;
  } else {
    isRecording = false;
  }
  _checkScreenShareUpdates(event.screenShares);
};

let _checkScreenShareUpdates = (shares) => {
  let diffArr = _arrDiff(screenShares, shares);
  if (diffArr.length !== 0) {
    if (screenShares.length > shares.length) {
      // a sceen share was removed.
      diffArr.forEach((stream) => {
        logger.debug(TAG, 'Screen share removed by ', (stream.peerId === peerId) ? 'this' : 'other', 'client');
        if (stream.peerId !== peerId) event_manager.emit(events.REMOTE_SCREEN_SHARE_REMOVED, stream);
      });
    } else if (screenShares.length < shares.length) {
      // a screen share was added.
      diffArr.forEach((stream) => {
        logger.debug(TAG, 'ScreenShare Added by ', (stream.peerId === peerId) ? 'this' : 'other', 'client');
        if (stream.peerId !== peerId) event_manager.emit(events.REMOTE_SCREEN_SHARE_ADDED, stream);
      });
    }
  }
  screenShares = shares;
};

let _arrDiff = (arr1, arr2) => {
  var filteredArr1 = arr1.filter(function(ele) {
    return arr2.indexOf(ele) == -1;
  });

  var filteredArr2 = arr2.filter(function(ele) {
    return arr1.indexOf(ele) == -1;
  });
  return filteredArr1.concat(filteredArr2);
};

let _notifyICEReset = () => {
  event_manager.emit(events.RESTARTING_ICE);
};

let _notifyConnected = (failed) => {
  if(failed){
    logger.error(TAG, 'Failed to restart ice - reconnecting socket');
    isConnected = false;
    signaling_module.connect(null, true,queryParams,() => {
      isConnected = true;
      // event_manager.emit(events.CONNECTED);
      // event_manager.emit(events.CONNECTED_TO_SESSION);
      peer_connection.restartIce();
    });
  }else{
    event_manager.emit(events.CONNECTED_TO_SESSION);
    isConnectedToRoom = true;
  }
};

/**
 * Module Interface
 */
module.exports = {
  init: init,
  on: (e, c) => {
    event_manager.on(e, c);
  },
  onOnce: (e, c) => {
    event_manager.once(e, c);
  },
  removeEventListener: (e, c) => {
    event_manager.removeListener(e, c);
  },
  connectToRoom: connectToRoom,
  events: events,
  changeDevice: changeDevice,
  startLocalMedia: startLocalMedia,
  stopLocalMedia: stopLocalMedia,
  stopStream: stopStream,
  startScreenShare: startScreenShare,
  stopScreenShare: stopScreenShare,
  enumerateDevices: enumerateDevices,
  toggleAudioMute: media_manager.toggleAudioMute,
  toggleVideoMute: toggleVideoMute,
  startRecording: startRecording,
  stopRecording: stopRecording,
  pauseRecording: pauseRecording,
  resumeRecording: resumeRecording,
  sendRecordingCustomCommand: sendRecordingCustomCommand,
  getStats: getStats,
  getStats2: getStats2,
  getSignalingId: getSignalingId,
  getSignalingVersion: getSignalingVersion,
  getMediaServerId: getMediaServerId,
  getMediaServerVersion: getMediaServerVersion,
  getMaxBw: getMaxBw,
  setDebugOn: () => {
    logger.setLevel(logger.DEBUG);
    logger.debug(TAG, 'Debug is turned on');
  },
  dispose: dispose,
  setPeerNickname: setPeerNickname,
  getPeerNickname: getPeerNickname,
  sendChatMessage: sendChatMessage,
  getDevicePreview: getDevicePreview,
  removePreview: removePreview,
  addPreviewToSession: addPreviewToSession,
  getPeerId: () => peerId,
  subscribeToPeer: subscribeToPeer,
  unsubscribeFromPeer: unsubscribeFromPeer,
  enterStage: enterStage,
  exitStage: exitStage,
  version: 'v0.2.7'
};



// WEBPACK FOOTER //
// ./src/Modules/switchrtc_core.js